import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Button } from '@magnetic/button'
import { Spinner } from '@magnetic/spinner'
import {
  ArrowsClockwise,
  Terminal,
  Play,
  Pause,
  Trash,
} from '@/lib/icons'
import { deriveSyncHealthFromState, getHealthIcon, getSyncIcon, shouldHaveCRD } from '@/utils/applicationStatus'
import type { Application as ProtoApplication, Project as ProtoProject } from '@/lib/generated-types'

type Application = Pick<ProtoApplication, 'displayName' | 'name' | 'argoName' | 'state' | 'deletedAt'>

interface ApplicationHeaderProps {
  application: Application
  project?: Pick<ProtoProject, 'awsRoleArn'> | null
  onSync: () => void
  onSuspend: () => void
  onResume: () => void
  onDelete: () => void
  onViewLogs: () => void
  onViewEcrSetup?: () => void
  isLoading: {
    sync: boolean
    suspend: boolean
    resume: boolean
    delete: boolean
  }
  logsLoading?: boolean
}

export function ApplicationHeader({
  application,
  project,
  onSync,
  onSuspend,
  onResume,
  onDelete,
  onViewLogs,
  onViewEcrSetup,
  isLoading,
  logsLoading = false,
}: ApplicationHeaderProps) {
  const { healthStatus, syncStatus } = deriveSyncHealthFromState(application.state)
  const isSuspended = application.state === 'Suspended'
  const isDeleting = !!application.deletedAt
  const hasCRD = shouldHaveCRD(application.state)
  const hasEcrSetup = !!(project?.awsRoleArn)

  return (
    <Flex align="center" justify="space-between">
      <Flex direction="vertical" gap="sm">
        <Flex align="center" gap={8}>
          <Heading size="primary">{application.displayName || application.name}</Heading>
          {getHealthIcon(healthStatus)}
          <Text size="p3">{healthStatus}</Text>
          {getSyncIcon(syncStatus)}
          <Text size="p3">{syncStatus}</Text>
        </Flex>
        <Text size="p4" color="light">{application.argoName}</Text>
      </Flex>
      <Flex gap="sm">
        {/* ECR & GitHub Actions - scroll to setup section */}
        {hasEcrSetup && onViewEcrSetup && (
          <Button
            kind="secondary"
            onClick={onViewEcrSetup}
          >
            ECR & GitHub Actions
          </Button>
        )}

        {/* Logs button - disabled if no CRD */}
        <Button
          kind="secondary"
          onClick={onViewLogs}
          disabled={logsLoading || !hasCRD}
          icon={logsLoading ? <Spinner size="sm" aria-label="Loading logs" /> : <Terminal size={16} />}
        >
          Logs
        </Button>

        {/* Sync button - only show if CRD exists and not suspended */}
        {hasCRD && !isSuspended && !isDeleting && (
          <Button
            onClick={onSync}
            disabled={isLoading.sync}
            icon={isLoading.sync ? <Spinner size="sm" aria-label="Syncing application" /> : <ArrowsClockwise size={16} />}
          >
            Sync
          </Button>
        )}

        {/* Resume button - only show if suspended */}
        {isSuspended && !isDeleting && (
          <Button
            kind="primary"
            onClick={onResume}
            disabled={isLoading.resume}
            icon={isLoading.resume ? <Spinner size="sm" aria-label="Resuming" /> : <Play size={16} />}
          >
            Resume
          </Button>
        )}

        {/* Suspend button - only show if not suspended and CRD exists */}
        {!isSuspended && hasCRD && !isDeleting && (
          <Button
            kind="secondary"
            onClick={onSuspend}
            disabled={isLoading.suspend}
            icon={isLoading.suspend ? <Spinner size="sm" aria-label="Suspending" /> : <Pause size={16} />}
          >
            Suspend
          </Button>
        )}

        {/* Delete button - show if not already deleting */}
        {!isDeleting && (
          <Button
            destructive
            onClick={onDelete}
            disabled={isLoading.delete}
            icon={<Trash size={16} />}
          >
            Delete
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
